/**
 * VRG-640 — the pure half of /internal/analytics's website section: labels, rates, date
 * presets (the funnel Sankey's half is websiteFunnelModel.ts, VRG-714). NO IMPORTS on purpose, so `test/website-analytics.guard.spec.ts`
 * can load it under node's own test runner (type-stripped) and pin the rules below.
 */

/**
 * THE labels the task file's Decisions §3 requires. Every surface that shows a number
 * says which population it counts, using these strings verbatim — a reader must never
 * have to guess whether "visitors" means everyone or only people who said yes.
 */
export const AUDIENCE_LABELS = {
  all: 'All visitors (Vercel)',
  // 2026-10-09: vera.inc consent is now by region (EU/UK and everyone non-US opt in; US is on
  // by default unless they opt out or send GPC), so "accepted" stopped being true for everyone.
  consented: 'Visitors with analytics on (PostHog)',
  vera: 'Signups to clients (Vera)',
} as const

export type Audience = 'all' | 'consented'

/**
 * Step-over-step conversion. Undefined (null), not 0 or 100%, whenever either side is
 * unmeasured or the previous step is zero — "0 of 0" is not a rate.
 */
export const conversion = (prev: number | null, cur: number | null): number | null =>
  prev === null || cur === null || prev <= 0 ? null : cur / prev

/** Where the funnel hands off: the Sales pipeline (GTM → Sales), built separately. */
export const SALES_PIPELINE_HANDOFF = '/internal/gtm/sales?tab=pipeline'

/*
  VRG-714 (2026-10-16): the four-phase funnel (buildFunnel / buildFunnelPhases, the
  Vercel/PostHog toggle and the "rates restart here" boundary) is gone. The funnel is now
  one Sankey of per-visitor paths from PostHog, with the waitlist joined per person on the
  server: see websiteFunnelModel.ts and apps/api website-funnel-sankey.ts.
*/

export const formatPct = (ratio: number | null): string =>
  ratio === null ? '—' : `${(ratio * 100).toFixed(ratio < 0.1 ? 1 : 0)}%`

export const formatCount = (n: number | null): string =>
  n === null ? '—' : n.toLocaleString('en-US')

export type RangePreset = 'today' | '7d' | '30d' | '90d' | '12m'

export const RANGE_PRESETS: ReadonlyArray<{ value: RangePreset; label: string; days: number }> = [
  { value: 'today', label: 'Today', days: 1 },
  { value: '7d', label: '7 days', days: 7 },
  { value: '30d', label: '30 days', days: 30 },
  { value: '90d', label: '90 days', days: 90 },
  { value: '12m', label: '12 months', days: 365 },
]

/** Spencer, 2026-10-10: the page opens on the last 7 days. */
export const DEFAULT_RANGE_PRESET: RangePreset = '7d'

/** localStorage key for the viewer's last range choice (a per-browser convenience). */
export const RANGE_STORAGE_KEY = 'vera.internal.website-analytics.range'

export const isRangePreset = (v: unknown): v is RangePreset =>
  typeof v === 'string' && RANGE_PRESETS.some((p) => p.value === v)

/**
 * The range the page opens on: the URL's (a shared link wins), else the viewer's last
 * choice, else the default. Garbage in either place is ignored, never an error.
 */
export const initialRangePreset = (fromUrl: string | null, stored: string | null): RangePreset =>
  isRangePreset(fromUrl) ? fromUrl : isRangePreset(stored) ? stored : DEFAULT_RANGE_PRESET

const isoDay = (d: Date): string => d.toISOString().slice(0, 10)

/**
 * Inclusive UTC day range ending today — except 'today', which starts at midnight in the
 * VIEWER's time zone: `from` carries that instant, and the API cuts PostHog and vera-2
 * there (Vercel only counts whole UTC days and says so). `since`/`until` are then the UTC
 * days spanning it, which is what the API derives from `from` anyway.
 */
export const rangeForPreset = (
  preset: RangePreset,
  now: Date = new Date(),
): { since: string; until: string; from?: string } => {
  if (preset === 'today') {
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    return { since: isoDay(midnight), until: isoDay(now), from: midnight.toISOString() }
  }
  const days = RANGE_PRESETS.find((p) => p.value === preset)?.days ?? 30
  const start = new Date(now.getTime())
  start.setUTCDate(start.getUTCDate() - (days - 1))
  return { since: isoDay(start), until: isoDay(now) }
}

/** A readable referrer: '' is direct traffic, not an unknown. */
export const referrerLabel = (value: string): string =>
  value === '' ? 'Direct / none' : value === 'Others' ? 'Others (outside the top 100)' : value

/**
 * The banner sentence for a source's state, or null when there is nothing to say. Kept
 * here so the wording for "not configured" is one decision, not one per card.
 */
export const sourceNotice = (
  name: string,
  state: { status: string; message?: string },
): string | null => {
  switch (state.status) {
    case 'fixture':
      return `${name}: FIXTURE data. This environment is not connected; the figures are invented for development.`
    case 'not_configured':
      return `${name}: not configured${state.message ? ` (${state.message})` : ''}.`
    case 'error':
      return `${name}: the request failed${state.message ? ` (${state.message})` : ''}. Figures from it are missing, not zero.`
    default:
      return state.message ? `${name}: ${state.message}.` : null
  }
}

// ── VRG-689: visitor history — time on page and recording-aware Replay ───────────────

/**
 * "34s", "2m 05s", "1h 02m". Null (unknown) is "—": the caller explains it, this never
 * turns an unknown into a zero. Seconds are rounded; under half a second reads "0s" only
 * when PostHog actually measured it.
 */
export const formatDuration = (seconds: number | null): string => {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return '—'
  const s = Math.round(seconds)
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
  return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`
}

export interface SessionTime {
  /** Sum of the pages whose time is known; null when none is known (or pages unreadable). */
  knownSeconds: number | null
  /** Pages whose time is unknown (no pageleave), so `knownSeconds` is a floor. */
  unknownPages: number
}

/** Per-session total: the known pages only, and how many were not known. Never invents the rest. */
export const sessionTime = (pages: ReadonlyArray<{ seconds: number | null }> | null): SessionTime => {
  if (!pages) return { knownSeconds: null, unknownPages: 0 }
  const known = pages.filter((p) => p.seconds !== null)
  return {
    knownSeconds: known.length ? known.reduce((a, p) => a + (p.seconds as number), 0) : null,
    unknownPages: pages.length - known.length,
  }
}

export type RecordingStatus = 'recorded' | 'none' | 'expired' | 'processing' | 'check_failed'

export interface ReplayView {
  /** Render the Replay link. */
  show: boolean
  /** Muted label when not shown, or a caveat tag beside a shown link. */
  label: string | null
  tooltip: string
  /** Glossary entry behind the tooltip (§44/§59). */
  term: string
}

/**
 * What the Replay column shows. Only `recorded` is a plain Replay; `none` and `expired` hide
 * it; `processing` and `check_failed` KEEP it (fail soft: a check that could not answer must
 * not hide a recording that may exist) and say so. A missing status (an API from before
 * VRG-689) behaves as before: Replay whenever there is a link.
 */
export const replayView = (recording: RecordingStatus | null | undefined, hasUrl: boolean): ReplayView => {
  switch (recording) {
    case 'recorded':
      return { show: hasUrl, label: null, tooltip: 'Open the recording in PostHog', term: 'website-no-recording' }
    case 'none':
      return {
        show: false,
        label: 'No recording',
        tooltip: "This visitor didn't allow session recording, or declined it.",
        term: 'website-no-recording',
      }
    case 'expired':
      return {
        show: false,
        label: 'No recording',
        tooltip: 'Recordings are kept 3 months. This session is older, or was never recorded.',
        term: 'website-no-recording',
      }
    case 'processing':
      return {
        show: hasUrl,
        label: 'may be unavailable',
        tooltip: 'Active minutes ago. PostHog may still be processing the recording, or there is none.',
        term: 'website-replay-unconfirmed',
      }
    case 'check_failed':
      return {
        show: hasUrl,
        label: 'may be unavailable',
        tooltip: 'Could not check PostHog for a recording. It may not exist.',
        term: 'website-replay-unconfirmed',
      }
    default:
      return { show: hasUrl, label: null, tooltip: 'Open the recording in PostHog', term: 'website-no-recording' }
  }
}
